import { z } from "zod";
import type { ExtractedAsset } from "./asset-extraction.ts";
import type { ListingLane } from "./config.ts";

export const AU_STATE_CODES = ["ACT", "NSW", "NT", "QLD", "SA", "TAS", "VIC", "WA"] as const;

export const GLOBAL_LISTING_TYPE_OPTIONS = [
  { value: "any", label: "Any" },
  { value: "sold", label: "Sold" },
  { value: "buy", label: "For Sale" },
  { value: "rent", label: "For Rent" },
] as const;

export const GLOBAL_TIME_RANGE_OPTIONS = [
  { value: "all", label: "All time" },
  { value: "last-7-days", label: "Last 7 days" },
  { value: "last-30-days", label: "Last 30 days" },
  { value: "last-90-days", label: "Last 90 days" },
  { value: "last-year", label: "Last year" },
  { value: "last-2-years", label: "Last 2 years" },
  { value: "last-5-years", label: "Last 5 years" },
  { value: "since-last-success", label: "Since last successful run" },
] as const;

export const GLOBAL_PROPERTY_TYPE_OPTIONS = [
  { value: "house", label: "House" },
  { value: "apartment-unit-flat", label: "Apartment / Unit / Flat" },
  { value: "townhouse", label: "Townhouse" },
  { value: "villa", label: "Villa" },
  { value: "land", label: "Land" },
  { value: "acreage", label: "Acreage" },
  { value: "rural", label: "Rural" },
  { value: "block-of-units", label: "Block of Units" },
] as const;

export const GlobalCrawlConfigSchema = z.object({
  listingType: z.enum(["any", "buy", "rent", "sold"]).optional(),
  propertyTypes: z.array(z.string()).optional(),
  dateFilterMode: z.enum(["relative", "absolute"]).optional(),
  timeRange: z.string().optional(),
  /** First-run window in days for timeRange "since-last-success". */
  rollingFirstRunDays: z.number().int().positive().optional(),
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
  minPrice: z.number().optional(),
  maxPrice: z.number().optional(),
  minBedrooms: z.number().int().nonnegative().optional(),
  maxBedrooms: z.number().int().nonnegative().optional(),
  minBathrooms: z.number().int().nonnegative().optional(),
  minParking: z.number().int().nonnegative().optional(),
  sortBy: z.string().optional(),
  excludeSurroundingSuburbs: z.boolean().optional(),
  excludePriceWithheld: z.boolean().optional(),
  manualUrl: z.union([z.string().url(), z.literal("")]).optional(),
});

export type GlobalCrawlConfig = z.infer<typeof GlobalCrawlConfigSchema>;
export type GlobalCrawlConfigInput = Partial<GlobalCrawlConfig>;

export interface SuburbInput {
  locality: string;
  postcode: string;
  state: string;
}

/**
 * One listing card read from a search-results page. Lets the discovery worker
 * reject out-of-scope URLs (e.g. sold > 5 years ago) without paying for a
 * detail-page scrape per URL.
 */
export interface DiscoveryPreview {
  url: string;
  address?: string;
  /** The street part of the address ("28/703 Orrong Road"), for address-key matching. */
  street?: string;
  suburb?: string;
  postcode?: string;
  state?: string;
  saleDate?: string;
  priceDisplay?: string;
  propertyType?: string;
  /** The portal's own listing id. */
  listingId?: string;
  bedrooms?: number;
  bathrooms?: number;
  parking?: number;
  /** Square metres. */
  landSize?: number;
  latitude?: number;
  longitude?: number;
  /** ISO date-time of a scheduled auction. */
  auctionAt?: string;
}

export interface AdapterMeta {
  slug: string;
  name: string;
  baseUrl: string;
  description: string;
  supportedStates: readonly string[] | null;
  urlPattern: RegExp;
  sourceType: "portal" | "agency";
}

export interface AdapterDefinition<
  ResolvedConfig extends Record<string, unknown> = Record<string, unknown>,
> {
  meta: AdapterMeta;
  defaultConfig: GlobalCrawlConfigInput;
  resolvedConfigSchema: z.ZodTypeAny;
  resolveConfig: (config?: GlobalCrawlConfigInput) => ResolvedConfig;
  buildSearchUrl: (suburb: SuburbInput, config: ResolvedConfig) => string;
  buildPaginatedUrl?: (searchUrl: string, page: number, config?: ResolvedConfig) => string;
  /**
   * Reads per-listing-card data (saleDate, suburb, propertyType) from a
   * search-results page's raw body (HTML, or JSON for a site API), so the
   * discovery worker can reject URLs outside the date window before
   * enqueueing them. Providers never extract these: every provider only
   * fetches raw pages (scrape-providers skill).
   *
   * Card URLs matching `meta.urlPattern` are discovered URLs too, so a page
   * whose links are not `<a href>`s (a JSON API, embedded data) still feeds
   * discovery. Return `null` for a page the parser does not recognise; `[]`
   * means a recognised page with no results, which ends pagination without
   * trying another provider.
   */
  parseDiscoveryPreviews?: (html: string, pageUrl: string) => DiscoveryPreview[] | null;
  /**
   * For a search page that is the same document whatever the suburb (Barry Plant's 12-month sold
   * sitemap, ~2 MB, fetched once per suburb task): the URL prefix naming that document, or null
   * for a page that must always be fetched. The discovery worker reads a matching discovery
   * snapshot stored in the last hour instead of fetching the page again.
   */
  discoveryCacheKey?: (pageUrl: string) => string | null;
  /**
   * Site-specific detail-page parser over the raw HTML every provider
   * returns. Its populated fields override our generic JSON-LD/regex parse
   * (`mergeAdapterParserData`); providers are never asked to parse.
   */
  parseDetailHtml?: (html: string, url: string) => Record<string, unknown>;
  /**
   * Site-specific bot-detection signature. Triggers a soft-block in the
   * scrape worker so the fallback provider chain gets a chance. Example:
   * REA serves priceDisplay="$700" with every other dynamic field empty
   * when it flags the request as a bot. Should return true only when the
   * data is unambiguously a bot-detection placeholder, never on partial
   * real data.
   */
  detectBotResponse?: (data: Record<string, unknown>) => boolean;
  /**
   * Search-result pages only: try Bright Data Web Unlocker first instead of escalating to it after
   * Firecrawl and Zyte are blocked. For portals whose search pages are bot-walled or JS-rendered.
   * Detail pages never go to Bright Data first: every adapter escalates a blocked detail page to
   * the one unlocker zone (`BRIGHTDATA_REA_COLLECTOR`). See the scrape-providers skill.
   */
  brightdataDiscoveryFirst?: boolean;
  /** The site refuses Zyte (REA answered every Zyte detail request with 503, 2026-09-26): skip it. */
  zyteBlocked?: boolean;
  /**
   * Search and detail pages try a plain direct fetch (free) before any paid provider; a failed or
   * empty direct fetch falls through to the normal chain and never counts as a block. Set only for
   * sites a "Direct fetch only" adapter preview has shown to serve full pages (agency backfill,
   * 2026-10-14).
   */
  directFirst?: boolean;
  /**
   * Listing types this adapter's search URL can express (the portals). Unset: the search is a
   * fixed sold page (every sold-only agency site), so each of its discovery tasks is a sold lane
   * whatever listing type the job names. See planDiscoveryLanes.
   */
  listingTypes?: readonly ListingLane[];
  /**
   * The lanes a job set to Any (or naming no listing type) crawls on this adapter, one discovery
   * task each. Default sold. George, 2026-10-15: crawl for-sale and sold, so a listing can be
   * followed until it sells; REA and Domain only, while allhomes and property.com.au stay
   * sold-only until each has an active-listing parse contract
   * (.cursor/plans/follow-until-sold-2026-10-15.md).
   */
  anyListingTypes?: readonly ListingLane[];
  /**
   * Direct-fetch timeout for a slow site (default 30 s). Buxton's listings API takes ~5 s per 50
   * sales unloaded and Realmark's search ~8 s; under backfill load both passed 30 s, and the page
   * fell through to paid providers those sites ban (runs d7355713, d91e722b).
   */
  directTimeoutMs?: number;
  /**
   * A detail page that finds no free Firecrawl seat goes straight to Bright Data instead of waiting
   * for a retry. Only for sites Bright Data serves reliably: Domain 33/35 in 20 min, REA 10/196
   * (2026-09-26), where failures also lower the zone's success rate and invite throttling.
   */
  brightdataOverflow?: boolean;
  /**
   * Buy-lane discovery records each search card in `listings` (record_listing_sightings) and
   * enqueues no detail pages: follow-until-sold phase 2, option B (George, 2026-10-26). Needs a
   * parseDiscoveryPreviews that reads for-sale cards. Domain and REA.
   */
  listingCards?: boolean;
  /**
   * Bright Data Scraping Browser (CDP/WSS) — for adapters whose listings
   * hydrate client-side via XHR (Next.js catch-all routes, SPAs) where
   * Web Unlocker (`format=raw`) returns only the
   * initial shell. The string is the env-var NAME holding the WSS URL
   * (e.g. `"BRIGHTDATA_SPA_BROWSER_WSS_URL"`); the worker resolves it at
   * runtime. Paid per-session — use sparingly. The chain selector
   * prepends `brightdata_browser` ahead of Web Unlocker when this is set.
   */
  brightdataBrowserZoneEnvVar?: string;
  /**
   * Site-specific asset extractor. Pulls photo / floorplan / PDF /
   * document URLs out of the listing detail HTML so the scrape worker
   * can download them into the property-assets storage bucket. When
   * undefined, callers should fall back to the generic extractor
   * (`extractGenericAssets`).
   */
  extractAssets?: (html: string, baseUrl: string) => ExtractedAsset[];
  /**
   * Direct API endpoint for discovery — bypasses the standard URL-fetch
   * + HTML-parse provider chain. When an adapter populates this field
   * the discover worker (and adapter-visibility) calls the endpoint
   * directly with a per-suburb-built request body and parses the JSON
   * response into property URLs. Use this when listings are JS-rendered
   * on top of a public JSON API (eg. eplace.com.au → Azure Cognitive
   * Search) — calling the API directly is more reliable than scraping
   * rendered HTML and avoids the Brightdata Web Unlocker per-request
   * cost. Per the snapshot-first architecture in
   * docs/snapshot-first-adapter-architecture.md, the raw JSON response
   * should also be persisted to page_snapshots so we can re-parse
   * without re-scraping.
   */
  directApiDiscovery?: {
    endpoint: string;
    method: "GET" | "POST";
    headers: Record<string, string>;
    buildBody: (
      suburb: SuburbInput,
      config: ResolvedConfig,
      options?: { skip?: number; top?: number },
    ) => Record<string, unknown> | undefined;
    parseResponse: (
      json: unknown,
      baseUrl: string,
    ) => { propertyUrls: string[]; totalCount?: number; hasNextPage: boolean };
  };
}

export interface AdapterRegistry {
  getAdapter: (slug: string) => AdapterDefinition<any> | undefined;
  getAllAdapters: () => AdapterDefinition<any>[];
  hasAdapter: (slug: string) => boolean;
  findAdapterForUrl: (url: string) => AdapterDefinition<any> | undefined;
  getDefaultConfig: (slug: string) => GlobalCrawlConfigInput;
  buildSearchUrl: (slug: string, suburb: SuburbInput, config?: GlobalCrawlConfigInput) => string;
  buildPaginatedUrl: (
    slug: string,
    searchUrl: string,
    page: number,
    config?: GlobalCrawlConfigInput,
  ) => string;
}

export function defineAdapter<ResolvedConfig extends Record<string, unknown>>(
  definition: AdapterDefinition<ResolvedConfig>,
): AdapterDefinition<ResolvedConfig> {
  return definition;
}
